import { useEffect, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCurrentProject } from 'src/providers/current-project.provider'
import { useAuth } from 'src/hooks/use-auth'
import { useSocket } from 'src/providers/socket.provider'
import { HumanReviewRequestsService } from 'src/services/human-review-requests.service'
import { EHumanReviewChangeType, EHumanReviewTab, type THumanReviewTab } from './human-review-requests.type'
import HumanReviewEmptyState from './human-review-empty-state.component'
import HumanReviewHeader from './human-review-header.component'
import HumanReviewFilters from './human-review-filters.component'
import HumanReviewTabs from './human-review-tabs.component'
import HumanReviewTabLayout from './human-review-tab-layout.component'
import { useMedEventFilterOptions } from './use-med-event-filter-options'
import { useHumanReviewTab } from './use-human-review-tab'

type THumanReviewUpdatedEvent =
  | {
      projectId: number
      changeType: EHumanReviewChangeType.CREATED | EHumanReviewChangeType.PATIENT_MED_EVENT_CHANGED
      patientMedEventId: number
    }
  | {
      projectId: number
      changeType: EHumanReviewChangeType.RESOLVED
      patientMedEventId: number
      resolvedByUserId: number
    }
  | {
      projectId: number
      changeType:
        | EHumanReviewChangeType.PATIENT_ARCHIVED
        | EHumanReviewChangeType.PATIENT_UNARCHIVED
        | EHumanReviewChangeType.PATIENT_DELETED
    }

const TAB_ORDER: EHumanReviewTab[] = [
  EHumanReviewTab.ALL,
  EHumanReviewTab.ADMINISTRATIVE_DOCUMENTS,
  EHumanReviewTab.MEDICAL_DOCUMENTS,
  EHumanReviewTab.CONVOCATION,
  EHumanReviewTab.POST_OP_CONTROL,
]

const EMPTY_TAB: THumanReviewTab = {
  subject: EHumanReviewTab.ALL,
  toProcessCount: 0,
  isVisible: false,
  problemCounts: {},
  resolutionCounts: {},
}

const noop = () => {}

const HumanReviewRequests = () => {
  const { project } = useCurrentProject()
  const { socket } = useSocket()
  const { currentUser } = useAuth()
  const queryClient = useQueryClient()
  const hasBeenConnectedRef = useRef(false)
  const hasLostConnectionRef = useRef(false)
  const { medEventGroups, allMedEventItems } = useMedEventFilterOptions()
  const [emptyStateSelectedMedEventIds, setEmptyStateSelectedMedEventIds] = useState<number[]>(() =>
    allMedEventItems.map((medEventItem) => medEventItem.value),
  )

  const { data } = useQuery({
    queryKey: ['human-review-requests-tabs', project.id],
    queryFn: () => HumanReviewRequestsService.getTabs(project.id),
  })

  const findTab = (subject: EHumanReviewTab) => data?.tabs.find((tab) => tab.subject === subject)
  const visibleSubjects = TAB_ORDER.filter((subject) => findTab(subject)?.isVisible)
  const { activeSubject, changeTab } = useHumanReviewTab(visibleSubjects)

  useEffect(() => {
    if (!socket) {
      hasLostConnectionRef.current = hasBeenConnectedRef.current
      return
    }
    hasBeenConnectedRef.current = true

    const invalidateTabsAndList = () => {
      queryClient.invalidateQueries({ queryKey: ['human-review-requests-tabs', project.id] })
      queryClient.invalidateQueries({ queryKey: ['human-review-requests-list', project.id] })
    }

    if (hasLostConnectionRef.current) {
      hasLostConnectionRef.current = false
      invalidateTabsAndList()
      queryClient.invalidateQueries({ queryKey: ['human-review-requests-detail', project.id] })
    }

    const handleHumanReviewUpdated = (event: THumanReviewUpdatedEvent) => {
      if (event.projectId !== project.id) return
      if (
        event.changeType === EHumanReviewChangeType.RESOLVED &&
        event.resolvedByUserId === currentUser?.id
      ) {
        return
      }
      invalidateTabsAndList()

      switch (event.changeType) {
        case EHumanReviewChangeType.CREATED:
        case EHumanReviewChangeType.PATIENT_MED_EVENT_CHANGED:
        case EHumanReviewChangeType.RESOLVED:
          queryClient.invalidateQueries({
            queryKey: ['human-review-requests-detail', project.id, event.patientMedEventId],
          })
          break
        case EHumanReviewChangeType.PATIENT_ARCHIVED:
        case EHumanReviewChangeType.PATIENT_DELETED:
          queryClient.removeQueries({ queryKey: ['human-review-requests-detail', project.id] })
          break
        case EHumanReviewChangeType.PATIENT_UNARCHIVED:
          queryClient.invalidateQueries({ queryKey: ['human-review-requests-detail', project.id] })
          break
      }
    }

    socket.on('HUMAN_REVIEW_REQUESTS_UPDATED', handleHumanReviewUpdated)

    return () => {
      socket.off('HUMAN_REVIEW_REQUESTS_UPDATED', handleHumanReviewUpdated)
    }
  }, [socket, project.id, currentUser?.id, queryClient])

  if (!data) return null

  if (visibleSubjects.length === 0) {
    return (
      <div className="c-flex c-flex-col c-flex-grow">
        <HumanReviewHeader totalCount={0} />
        <HumanReviewFilters
          activeTab={EMPTY_TAB}
          medEventGroups={medEventGroups}
          allMedEventItems={allMedEventItems}
          selectedProblems={[]}
          selectedResolutions={[]}
          selectedMedEventIds={emptyStateSelectedMedEventIds}
          onSelectStatuses={noop}
          onSelectMedEvents={setEmptyStateSelectedMedEventIds}
        />
        <div className="c-flex c-flex-grow c-border-t c-border-grey-300">
          <HumanReviewEmptyState />
        </div>
      </div>
    )
  }

  // The URL must always tell which tab is displayed: nothing is rendered until the hook has corrected it
  if (!activeSubject) return null

  const activeTabData = findTab(activeSubject)!
  const totalCount = findTab(EHumanReviewTab.ALL)?.toProcessCount ?? 0

  return (
    <div className="c-flex c-flex-col">
      <HumanReviewHeader totalCount={totalCount} />
      {visibleSubjects.length > 1 && (
        <HumanReviewTabs
          tabs={visibleSubjects.map((subject) => findTab(subject)!)}
          activeTab={activeSubject}
          onTabChange={changeTab}
        />
      )}
      <HumanReviewTabLayout subject={activeSubject} activeTab={activeTabData} onTabChange={changeTab} />
    </div>
  )
}

export default HumanReviewRequests
